import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAppSelector } from "redux/hooks";

interface JvPartnerTableProps {
  mode?: string;
  jvPartnerData: any[];
  handleJvPartnerDataChange: (index: number, field: string, value: any) => void;
  addJvPartner: () => void;
  removeJvPartner: (index: number) => void;
}

export const JvPartnerTable: React.FC<JvPartnerTableProps> = ({
  mode,
  jvPartnerData,
  handleJvPartnerDataChange,
  addJvPartner,
  removeJvPartner,
}) => {
  const dapData = useAppSelector((state) => state.summary.dapData);
  const [expandedPanels, setExpandedPanels] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    // Clean up expanded panels when items are removed
    if (jvPartnerData && jvPartnerData.length > 0) {
      const validIndices = jvPartnerData.map((_, index) => index);
      setExpandedPanels(prev => {
        const cleaned = {};
        validIndices.forEach(index => {
          if (prev[index]) {
            cleaned[index] = prev[index];
          }
        });
        return cleaned;
      });
    } else {
      setExpandedPanels({});
    }
  }, [jvPartnerData?.length]);

  const handleAccordionChange = (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanels(prev => ({ ...prev, [index]: isExpanded }));
  };

  return (
    <div style={{ marginBottom: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h3 style={{ marginTop: '0px', marginBottom: '0px' }}>JV Partner(s)</h3>
      </div>
      {jvPartnerData && jvPartnerData.length > 0 ? jvPartnerData.map((partner, index) => (
        <Accordion
          key={index}
          expanded={expandedPanels[index] || false}
          onChange={handleAccordionChange(index)}
          sx={{ marginBottom: 1, boxShadow: 1 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
            sx={{ backgroundColor: '#253746', color: 'white' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Typography>JV Partner {index + 1}</Typography>
              {mode === "edit" &&
                <div>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      addJvPartner();
                    }}
                    sx={{
                      minWidth: '20px',
                      height: '20px',
                      padding: '0',
                      marginRight: '8px',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      borderRadius: '20%',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: '#45a049',
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <AddIcon sx={{ fontSize: '16px' }} />
                  </Button>
                  {jvPartnerData.length > 1 && (
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeJvPartner(index);
                      }}
                      sx={{
                        minWidth: '20px',
                        height: '20px',
                        padding: '0',
                        backgroundColor: '#f44336',
                        color: 'white',
                        borderRadius: '20%',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: '#da190b',
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: '16px' }} />
                    </Button>
                  )}

                </div>
              }
            </div>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 0,
                '& .MuiTable-root': {
                  borderCollapse: 'collapse',
                  minWidth: '100%',
                },
                '& .MuiTableCell-root': {
                  border: '1px solid rgba(224, 224, 224, 1)',
                  padding: '4px 16px',
                  fontSize: '0.875rem',
                  height: '40px'
                },
                '& .MuiTableRow-root': {
                  height: '40px'
                }
              }}
            >
              <Table
                aria-label={`jv partner ${index + 1} table`}
                size="small"
                sx={{ '& .MuiTableCell-sizeSmall': { padding: '2px 16px' } }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={2}  sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>If the JV partner is also the PrM, ensure you complete the assessment of them as a PrM as well.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Please list the JV partner firm <span style={{ color: 'red' }}>*</span></TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        disabled={mode !== "edit"}
                        multiline
                        value={partner.jvPartnerFirmName || ''}
                        onChange={(e) => handleJvPartnerDataChange(index, "jvPartnerFirmName", e.target.value)}
                        InputProps={{
                          style: {
                            margin: "0px",
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            fontSize: "0.875rem",
                            color: "rgba(0, 0, 0, 0.6)",
                          },
                        }}
                        sx={{ '& .MuiInputBase-root': { minHeight: '32px' } }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Is this partner the developer?</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={jvPartnerData[index]?.isPartnerDeveloper === true ? "yes" : jvPartnerData[index]?.isPartnerDeveloper === false ? "no" : undefined}
                          size="small"
                          displayEmpty
                          disabled={mode !== "edit"}
                          onChange={(e) => handleJvPartnerDataChange(index, "isPartnerDeveloper", e.target.value === "yes")}
                          sx={{
                            height: '32px',
                            '& .MuiSelect-select': {
                              margin: "0px",
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                              padding: '4px 14px',
                            }
                          }}
                        >
                          <MenuItem value={undefined} disabled>Select Y/N</MenuItem>
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Would you work with this partner again?</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={jvPartnerData[index]?.willWorkWithJvPartnerAgain === true ? "yes" : jvPartnerData[index]?.willWorkWithJvPartnerAgain === false ? "no" : undefined}
                          size="small"
                          displayEmpty
                          disabled={mode !== "edit"}
                          onChange={(e) => handleJvPartnerDataChange(index, "willWorkWithJvPartnerAgain", e.target.value === "yes")}
                          sx={{
                            height: '32px',
                            '& .MuiSelect-select': {
                              margin: "0px",
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                              padding: '4px 14px',
                            }
                          }}
                        >
                          <MenuItem value={undefined} disabled>Select Y/N</MenuItem>
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Key Contact</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        disabled={mode !== "edit"}
                        value={partner.keyContact || ''}
                        onChange={(e) => handleJvPartnerDataChange(index, "keyContact", e.target.value)}
                        InputProps={{
                          style: {
                            margin: "0px",
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            fontSize: "0.875rem",
                            color: "rgba(0, 0, 0, 0.6)",
                          },
                        }}
                        sx={{ '& .MuiInputBase-root': { height: '32px' } }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        disabled={mode !== "edit"}
                        value={partner.email || ''}
                        onChange={(e) => handleJvPartnerDataChange(index, "email", e.target.value)}
                        InputProps={{
                          style: {
                            margin: "0px",
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            fontSize: "0.875rem",
                            color: "rgba(0, 0, 0, 0.6)",
                          },
                        }}
                        sx={{ '& .MuiInputBase-root': { height: '32px' } }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Comments</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        disabled={mode !== "edit"}
                        multiline
                        value={jvPartnerData[index]?.comments || ''}
                        onChange={(e) => handleJvPartnerDataChange(index, "comments", e.target.value)}
                        InputProps={{
                          style: {
                            margin: "0px",
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            fontSize: "0.875rem",
                            color: "rgba(0, 0, 0, 0.6)",
                          },
                        }}
                        sx={{ '& .MuiInputBase-root': { minHeight: '32px' } }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ height: '10px' }}></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2}   sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Rank the JV partner on the topics below with 1 being the worst and 5 being the best.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Responsiveness</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={jvPartnerData[index]?.responsiveness || undefined}
                          size="small"
                          displayEmpty
                          disabled={mode !== "edit"}
                          onChange={(e) => handleJvPartnerDataChange(index, "responsiveness", e.target.value)}
                          sx={{
                            height: '32px',
                            '& .MuiSelect-select': {
                              margin: "0px",
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                              padding: '4px 14px',
                            }
                          }}
                        >
                          <MenuItem value={undefined} disabled>rank 1-5</MenuItem>
                          <MenuItem value={1}>1</MenuItem>
                          <MenuItem value={2}>2</MenuItem>
                          <MenuItem value={3}>3</MenuItem>
                          <MenuItem value={4}>4</MenuItem>
                          <MenuItem value={5}>5</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Reporting</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={jvPartnerData[index]?.reporting || undefined}
                          size="small"
                          displayEmpty
                          disabled={mode !== "edit"}
                          onChange={(e) => handleJvPartnerDataChange(index, "reporting", e.target.value)}
                          sx={{
                            height: '32px',
                            '& .MuiSelect-select': {
                              margin: "0px",
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                              padding: '4px 14px',
                            }
                          }}
                        >
                          <MenuItem value={undefined} disabled>rank 1-5</MenuItem>
                          <MenuItem value={1}>1</MenuItem>
                          <MenuItem value={2}>2</MenuItem>
                          <MenuItem value={3}>3</MenuItem>
                          <MenuItem value={4}>4</MenuItem>
                          <MenuItem value={5}>5</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Flexibility</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={jvPartnerData[index]?.flexibility || undefined}
                          size="small"
                          displayEmpty
                          disabled={mode !== "edit"}
                          onChange={(e) => handleJvPartnerDataChange(index, "flexibility", e.target.value)}
                          sx={{
                            height: '32px',
                            '& .MuiSelect-select': {
                              margin: "0px",
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                              padding: '4px 14px',
                            }
                          }}
                        >
                          <MenuItem value={undefined} disabled>rank 1-5</MenuItem>
                          <MenuItem value={1}>1</MenuItem>
                          <MenuItem value={2}>2</MenuItem>
                          <MenuItem value={3}>3</MenuItem>
                          <MenuItem value={4}>4</MenuItem>
                          <MenuItem value={5}>5</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Ethics</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={jvPartnerData[index]?.ethics || undefined}
                          size="small"
                          displayEmpty
                          disabled={mode !== "edit"}
                          onChange={(e) => handleJvPartnerDataChange(index, "ethics", e.target.value)}
                          sx={{
                            height: '32px',
                            '& .MuiSelect-select': {
                              margin: "0px",
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                              padding: '4px 14px',
                            }
                          }}
                        >
                          <MenuItem value={undefined} disabled>rank 1-5</MenuItem>
                          <MenuItem value={1}>1</MenuItem>
                          <MenuItem value={2}>2</MenuItem>
                          <MenuItem value={3}>3</MenuItem>
                          <MenuItem value={4}>4</MenuItem>
                          <MenuItem value={5}>5</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Communication</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={jvPartnerData[index]?.communication || undefined}
                          size="small"
                          displayEmpty
                          disabled={mode !== "edit"}
                          onChange={(e) => handleJvPartnerDataChange(index, "communication", e.target.value)}
                          sx={{
                            height: '32px',
                            '& .MuiSelect-select': {
                              margin: "0px",
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                              padding: '4px 14px',
                            }
                          }}
                        >
                          <MenuItem value={undefined} disabled>rank 1-5</MenuItem>
                          <MenuItem value={1}>1</MenuItem>
                          <MenuItem value={2}>2</MenuItem>
                          <MenuItem value={3}>3</MenuItem>
                          <MenuItem value={4}>4</MenuItem>
                          <MenuItem value={5}>5</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Collaboration</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={jvPartnerData[index]?.collaboration || undefined}
                          size="small"
                          displayEmpty
                          disabled={mode !== "edit"}
                          onChange={(e) => handleJvPartnerDataChange(index, "collaboration", e.target.value)}
                          sx={{
                            height: '32px',
                            '& .MuiSelect-select': {
                              margin: "0px",
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                              padding: '4px 14px',
                            }
                          }}
                        >
                          <MenuItem value={undefined} disabled>rank 1-5</MenuItem>
                          <MenuItem value={1}>1</MenuItem>
                          <MenuItem value={2}>2</MenuItem>
                          <MenuItem value={3}>3</MenuItem>
                          <MenuItem value={4}>4</MenuItem>
                          <MenuItem value={5}>5</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Comments</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        disabled={mode !== "edit"}
                        multiline
                        value={jvPartnerData[index]?.rankComments || ''}
                        onChange={(e) => handleJvPartnerDataChange(index, "rankComments", e.target.value)}
                        InputProps={{
                          style: {
                            margin: "0px",
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            fontSize: "0.875rem",
                            color: "rgba(0, 0, 0, 0.6)",
                          },
                        }}
                        sx={{ '& .MuiInputBase-root': { minHeight: '32px' } }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ height: '10px' }}></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Is the partner also the Property Manager?</TableCell>
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={jvPartnerData[index]?.isJvPartnerAlsoPrM === true ? "yes" : jvPartnerData[index]?.isJvPartnerAlsoPrM === false ? "no" : undefined}
                          size="small"
                          displayEmpty
                          disabled={mode !== "edit"}
                          onChange={(e) => handleJvPartnerDataChange(index, "isJvPartnerAlsoPrM", e.target.value === "yes")}
                          sx={{
                            height: '32px',
                            '& .MuiSelect-select': {
                              margin: "0px",
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "0.875rem",
                              color: "rgba(0, 0, 0, 0.6)",
                              padding: '4px 14px',
                            }
                          }}
                        >
                          <MenuItem value={undefined} disabled>Select Y/N</MenuItem>
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )) : (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          No JV Partners added yet.
        </div>
      )}
    </div>
  );
};